import React, { useState, useEffect } from "react";
import { TextField, Typography, FormControl, Box, Dialog, Select, MenuItem, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import dayjs from "dayjs";
import { Button } from '../Button'
import { Text } from '../Typography';
import { useTranslation } from 'react-i18next';
import { useDataProvider } from "src/mtska-frontend-data-provider";

const MultiBookingFormDialog = ({ open, onClose, dateRange, possibleDrivers, onComplete }) => {
    const { t } = useTranslation();
    const [cta, setCta] = useState(t('Select which vehicle you want to book'));
    const [selectedCalendarId, setSelectedCalendarId] = useState('');
    const [atLeastOneCalendar, setAtLeastOneCalendar] = useState('');
    const [localSaving, setLocalSaving] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [errorInfo, setErrorInfo] = useState();
    const [requestStartAt, setRequestStartAt] = useState();
    const [requestEndAt, setRequestEndAt] = useState();
    const [bookingStartAt, setBookingStartAt] = useState();
    const [bookingEndAt, setBookingEndAt] = useState();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // DATA PROVIDER
    const {
        doAction,
        success,
        errorMessage
    } = useDataProvider('calendars/carcalendarrecord', false);

    const {
        items: bookableCalendars,
        loadAll
    } = useDataProvider('calendars/carcalendar/listavailability', false);

    const handleSave = async (data) => {
        doAction && doAction({ action: 'create', model: 'calendars/carcalendarrecord', data: data });
    }

    useEffect(() => {
        if (dateRange[0] == null || dateRange[1] == null) {
            return;
        };

        const ranged = {
            bookingStart_at: dateRange[0].format('YYYY-MM-DDTHH:mm:ss'),
            bookingEnd_at: dateRange[1].format('YYYY-MM-DDTHH:mm:ss'),
        };

        setBookingStartAt(dateRange[0]);
        setBookingEndAt(dateRange[1]);
        setRequestStartAt(dateRange[0]);
        setRequestEndAt(dateRange[1]);
        setSelectedCalendarId('');
        setAtLeastOneCalendar('');

        loadAll(ranged);
    }, [dateRange])

    useEffect(() => {
        if (success) {
            //onSave()
            onComplete();
            setIsCompleted(true);
        }
        setLocalSaving(false);
    }, [success])

    useEffect(() => {
        if (errorMessage.length > 0) {
            setLocalSaving(false);
            setErrorInfo(errorMessage);
        }
    }, [errorMessage])

    useEffect(() => {
        const aloc = bookableCalendars.find(bookableCalendar =>
            bookableCalendar?.car_id &&
            Array.isArray(bookableCalendar.available_ranges) &&
            bookableCalendar.available_ranges.length > 0
        ) !== undefined;

        setAtLeastOneCalendar(aloc);
    }, [bookableCalendars])

    const getCustomMessage = () => {
        const bookableCalendar = bookableCalendars.find((bc) => bc._id === selectedCalendarId);
        if (!bookableCalendar) return;

        return (
            <Box className="bookingInfo">
                {/* <textarea>{JSON.stringify(bookableCalendar)}</textarea> */}
                <p className="customMessage">{bookableCalendar.customMessage}</p>
                {/* {
                    bookableCalendar.available_ranges[0].notes && <p className="errorMessage">{bookableCalendar.available_ranges[0].notes}</p>
                } */}

                <p className="timeframePickup"><span className="label">{t('timeframePickup')}</span><span className="value">{bookableCalendar.timeframePickup}</span></p>
                <p className="timeframeReenter"><span className="label">{t('timeframeReenter')}</span><span className="value">{bookableCalendar.timeframeReenter}</span></p>
            </Box>
        )
    }

    const handleClose = (e) => {
        e.preventDefault();
        if (isCompleted) {
            setIsCompleted(false);
        }
        onClose();
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const bookableCalendar = bookableCalendars.find(d => d._id === selectedCalendarId);

        const data = {
            bookingStart_at: bookingStartAt.format('YYYY-MM-DD'),
            bookingEnd_at: bookingEndAt.format('YYYY-MM-DD'),
            isApproved: 'true',
            car_calendar_id: selectedCalendarId,
            employment_id: possibleDrivers._id,
            legal_entity_id: bookableCalendar.legal_entity_id,
            notes: formData.get('notes')
        }

        handleSave(data);
        setLocalSaving(true);
    };

    const handleCalendarSelection = (e) => {
        setSelectedCalendarId(e.target.value);
        const bookableCalendar = bookableCalendars.find(d => d._id === e.target.value);

        setBookingStartAt(dayjs(bookableCalendar.available_ranges[0].bookingStart_at));
        setBookingEndAt(dayjs(bookableCalendar.available_ranges[0].bookingEnd_at));

    }

    return (
        <Dialog className="dialog dialog-confirm dialogMultiBookingForm" open={open} onClose={handleClose}>
            <Box className="dialog-backdrop"></Box>
            {isCompleted && (
                <Box className="dialog-content">
                    <Text level="h3" style={{ fontSize: '18px' }}>{t('New booking: {{bookingStart_at}} - {{bookingEnd_at}}', { bookingStart_at: bookingStartAt?.format('DD/MM/YYYY'), bookingEnd_at: bookingEndAt?.format('DD/MM/YYYY') })}</Text>

                    <Text level="body-md" style={{ fontSize: '14px' }}>{t('Booking completed, check your email to find the confirmation message with all needed details.')}</Text>

                    <Box className="dialog-actions" style={{ paddingTop: '30px' }}>
                        <Button variant="solid" className="dialog-ok" onClick={handleClose}>{t('Close')}</Button>
                    </Box>
                </Box>
            )}
            {!isCompleted && (
                <Box className="dialog-content">
                    <Text level="h3" style={{ fontSize: '18px' }}>{t('New booking: {{bookingStart_at}} - {{bookingEnd_at}}', { bookingStart_at: bookingStartAt?.format('DD/MM/YYYY'), bookingEnd_at: bookingEndAt?.format('DD/MM/YYYY') })}</Text>

                    {
                        errorInfo && (
                            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                ⚠️ {errorInfo}
                            </Typography>
                        )
                    }
                    {
                        selectedCalendarId && ((bookingStartAt && !bookingStartAt.isSame(requestStartAt)) || (bookingEndAt && !bookingEndAt.isSame(requestEndAt))) && (

                            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                ⚠️ {t('Your request: {{bookingStart_at}} - {{bookingEnd_at}}', { bookingStart_at: requestStartAt?.format('DD/MM/YYYY'), bookingEnd_at: requestEndAt?.format('DD/MM/YYYY') })}
                            </Typography>
                        )
                    }
                    {
                        atLeastOneCalendar === true &&
                        (
                            <form onSubmit={handleSubmit} id="booking-form" style={{ paddingTop: '20px' }}>

                                {
                                    !selectedCalendarId && <Text level="body-md" style={{ fontSize: '14px' }}>{cta}</Text>
                                }

                                <FormControl fullWidth sx={{ mb: 2 }}>

                                    <Select
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    maxWidth: '80vw', // non superare la larghezza viewport
                                                    overflowX: 'auto',  // se il contenuto è troppo largo
                                                },
                                            },
                                        }}
                                        value={selectedCalendarId}
                                        onChange={handleCalendarSelection}
                                        renderValue={(selected) => {
                                            const bookableCalendar = bookableCalendars.find(d => d._id === selected);

                                            return bookableCalendar ? (
                                                <Box>
                                                    <Typography sx={{ fontSize: '16px', fontWeight: '700' }}>{bookableCalendar.car_plate}</Typography>
                                                    <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>{bookableCalendar.car_searchable.replace(bookableCalendar.car_plate, '')}</Typography>
                                                    <Typography sx={{ fontSize: '10px', fontWeight: '400', backgroundColor: '#AFBBD9', color: '#FFF', padding: '0 5px', borderRadius: '4px', lineHeight: '24px', marginTop: '5px', marginLeft: '-5px', display: 'inline-block' }}>
                                                        {t('Available from {{bookingStart_at}} to {{bookingEnd_at}}', {
                                                            bookingStart_at: dayjs(bookableCalendar.available_ranges[0].bookingStart_at).format('DD/MM/YYYY'),
                                                            bookingEnd_at: dayjs(bookableCalendar.available_ranges[0].bookingEnd_at).format('DD/MM/YYYY'),
                                                        })}
                                                    </Typography>
                                                </Box>
                                            ) : null;
                                        }}
                                    >
                                        {bookableCalendars.map((bookableCalendar) => {
                                            if (!bookableCalendar?.car_id) {
                                                return
                                            };
                                            if (!Array.isArray(bookableCalendar?.available_ranges) || bookableCalendar?.available_ranges.length < 1) {
                                                return;
                                            }

                                            return (
                                                <MenuItem key={bookableCalendar._id} value={bookableCalendar._id}>
                                                    <Box className="optionWrapper">
                                                        <Typography sx={{ fontSize: '16px', fontWeight: '700', overflowWrap: 'anywhere' }}>{bookableCalendar.car_plate}</Typography>
                                                        <Typography sx={{ fontSize: '14px', fontWeight: '400', overflowWrap: 'anywhere' }}>{bookableCalendar.car_searchable.replace(bookableCalendar.car_plate, '')}</Typography>
                                                        <Typography sx={{ fontSize: '10px', fontWeight: '400', overflowWrap: 'anywhere', backgroundColor: '#AFBBD9', color: '#FFF', padding: '0 5px', borderRadius: '4px', lineHeight: '24px', marginTop: '5px', marginLeft: '-5px', display: 'inline-block' }}>
                                                            {t('Available from {{bookingStart_at}} to {{bookingEnd_at}}', {
                                                                bookingStart_at: dayjs(bookableCalendar.available_ranges[0].bookingStart_at).format('DD/MM/YYYY'),
                                                                bookingEnd_at: dayjs(bookableCalendar.available_ranges[0].bookingEnd_at).format('DD/MM/YYYY'),
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            )
                                        })}
                                    </Select>

                                </FormControl>

                                {
                                    getCustomMessage()
                                }

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0' }}>
                                    <TextField label="Note" name="notes" multiline rows={4} fullWidth />
                                </div>
                            </form>
                        )
                    }

                    {
                        atLeastOneCalendar === false &&
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>{atLeastOneCalendar}
                            {t('Sorry, but no vehicles are available for the selected days')}
                        </Typography>
                    }

                    {
                        atLeastOneCalendar === '' &&
                        <CircularProgress />
                    }

                    <Box className="dialog-actions" style={{ paddingTop: '10px' }}>
                        <Button variant="soft" className="dialog-ko" onClick={handleClose}>{t('Cancel')}</Button>
                        <Button variant="solid" className="dialog-ok" type="submit" form="booking-form" disabled={!selectedCalendarId || localSaving}>{t('Save')}</Button>
                    </Box>
                </Box>
            )}
        </Dialog >
    )

};

export default MultiBookingFormDialog;